import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, RefreshCw } from "lucide-react";
import Layout from "@/components/layout/Layout";
import api, { formatApiError, mediaUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { GROMMET_SIDE_CHOICES, GROMMET_SPACING_CHOICES, isMirrorPrint, optionOn, optionSummary, priceNoteOf, rateCaption, sizeLabel } from "@/lib/signs";
import { captureProof } from "@/lib/proof";
import SignCanvas from "@/components/signs/SignCanvas";
import DesignerBar, { PriceTable, SheetTable } from "@/components/signs/DesignerBar";
import ImageRail from "@/components/signs/ImageRail";
import OptionDock from "@/components/signs/OptionDock";
import { ChoiceField } from "@/components/signs/Fields";
import { useSignConfig } from "@/components/signs/useSignConfig";
import { useMall } from "@/context/MallContext";
import { cardTheme } from "@/lib/mall";

const WALL = "#eef0f3";

/** Front / back switch that floats over the canvas on a double-sided sign. */
function SideTabs({ view, setView, theme }) {
    return (
        <div className="absolute left-4 top-4 z-10 flex rounded-full border border-zinc-200 bg-white p-0.5 shadow-sm" data-testid="side-tabs">
            {["front", "back"].map((s) => (
                <button key={s} type="button" onClick={() => setView(s)} data-testid={`side-tab-${s}`}
                    className="rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider transition-colors"
                    style={view === s ? { background: theme.primary, color: theme.on } : { color: "#71717a" }}>
                    {s}
                </button>
            ))}
        </div>
    );
}

/** Turns the sign over, under the artboard, with a word on which side you are looking at. */
function FlipOver({ view, setView, theme }) {
    return (
        <div className="absolute bottom-4 left-4 z-10 flex items-center gap-2" data-testid="flip-controls">
            <button type="button" onClick={() => setView(view === "front" ? "back" : "front")} data-testid="flip-over-btn"
                className="inline-flex items-center gap-1.5 rounded-full border bg-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider shadow-sm transition-colors hover:shadow"
                style={{ borderColor: theme.primary, color: theme.ink || theme.primary }}>
                <RefreshCw size={12} /> Flip over
            </button>
            <span className="rounded-full bg-white/90 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-600 shadow-sm" data-testid="side-caption">
                {view} side
            </span>
        </div>
    );
}

/** Where the grommets go and how far apart — shows under the sign only while grommets are on. */
function GrommetControls({ cfg, theme }) {
    return (
        <div className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 gap-2 rounded-2xl border border-zinc-200 bg-white/95 p-1.5 shadow-lg backdrop-blur" data-testid="grommet-controls">
            <ChoiceField label="Grommet placement" theme={theme} testid="field-grommet-sides" choices={GROMMET_SIDE_CHOICES}
                value={cfg.options.grommet_sides || "top_bottom"} onChange={(v) => cfg.setOption("grommet_sides", v)} />
            <ChoiceField label="Spacing" theme={theme} testid="field-grommet-spacing" choices={GROMMET_SPACING_CHOICES}
                value={cfg.options.grommet_spacing || "every_2_3_ft"} onChange={(v) => cfg.setOption("grommet_spacing", v)} />
        </div>
    );
}

/** The sign designer: one bar, images on the left, the sign in the middle, options along the bottom. Fits one screen. */
export default function SignProduct() {
    const { category, slug } = useParams();
    const { addItem, setOpen } = useCart();
    const { store: activeStore, ensureStore, enter } = useMall();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState(false);
    const stageRef = useRef(null);

    useEffect(() => { ensureStore(); }, [ensureStore]);

    useEffect(() => {
        setLoading(true);
        api.get(`/signs/products/${slug}`)
            .then(({ data }) => setData(data))
            .catch(() => toast.error("That product could not be found"))
            .finally(() => setLoading(false));
    }, [slug]);

    const product = data?.product;
    const productStore = product?.store;
    useEffect(() => {
        if (!activeStore && productStore?.slug) enter(productStore, "product");
    }, [activeStore, productStore, enter]);
    const cfg = useSignConfig(product);

    const add = async () => {
        setAdding(true);
        try {
            const proof = await captureProof(stageRef.current, `${product.slug}-proof`, WALL);
            const q = cfg.quote;
            const variant = (product.variants || []).find((v) => v.id === cfg.variantId);
            addItem({
                product_slug: product.slug,
                product_name: product.name,
                image: mediaUrl(product.image_url),
                proof,
                quantity: q.quantity,
                unit_price: q.unit_price,
                line_total: q.line_total,
                summary: [sizeLabel(cfg.width, cfg.height), variant?.label, ...optionSummary(product, cfg.options)].filter(Boolean).join(" · "),
                store: product.store,
                selections: {
                    kind: "sign",
                    store_slug: product.store?.slug || product.store_slug,
                    variant_id: cfg.variantId, preset_id: cfg.presetId,
                    width: Number(cfg.width) || 0, height: Number(cfg.height) || 0,
                    quantity: Number(cfg.quantity) || 0, options: cfg.options,
                    art_transform: cfg.sides.front.transform, art_transform_back: cfg.sides.back.file ? cfg.sides.back.transform : undefined,
                    actual_size: cfg.actual ? { width: cfg.actual.w, height: cfg.actual.h } : undefined,
                },
                artwork: cfg.artwork,
            });
            toast.success("Added to cart");
            setOpen(true);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setAdding(false);
        }
    };

    if (loading) {
        return <Layout designer><div className="flex min-h-[60vh] items-center justify-center text-zinc-400"><Loader2 className="animate-spin" /></div></Layout>;
    }
    if (!product) {
        return (
            <Layout designer>
                <div className="mx-auto max-w-3xl px-4 py-24 text-center">
                    <h1 className="font-display text-3xl font-black">Product not found</h1>
                    <Link to="/signs" className="mt-4 inline-block underline">Back to all signs</Link>
                </div>
            </Layout>
        );
    }

    const theme = cardTheme({ store: product.store }, activeStore);
    const variant = (product.variants || []).find((v) => v.id === cfg.variantId);
    const doubleSided = variant?.print_sides === "double" || cfg.options.print_sides_opt === "double-sided";
    const view = doubleSided ? cfg.view : "front";
    const side = cfg.sides[view];
    const showGrommets = optionOn(cfg.options.grommets) && Number(cfg.width) > 0 && Number(cfg.height) > 0;
    const q = cfg.quote;
    const needsArt = !cfg.artwork.length;
    const needsBack = doubleSided && !cfg.sides.back.file;
    const priceNote = priceNoteOf(product.pricing_model, q);
    const handheld = (category || product.category) === "handheld";

    return (
        <Layout designer>
            <div className="flex flex-col bg-white lg:h-[calc(100vh-148px)] lg:overflow-hidden" data-testid="sign-designer" data-category={category || product.category}>
                <DesignerBar back={`/signs/${data.category?.slug || product.category}`} backLabel={data.category?.name || "Signs"}
                    title={product.name} description={product.short_description} store={product.store} turnaround={data.turnaround} theme={theme}
                    price={q?.line_total} priceNote={priceNote} errors={[...(q?.errors || []), needsArt && "Upload your image", !needsArt && needsBack && "Upload the back image (or use the front one)"]} sale={q?.sale}
                    busy={cfg.pricing} blocked={cfg.pricing || (q?.errors || []).length > 0 || needsArt || needsBack || adding} onAdd={add} adding={adding}>
                    <PriceTable variants={product.variants || []} variantId={cfg.variantId} theme={theme} />
                    <SheetTable product={product} quote={q} options={cfg.options} theme={theme} />
                </DesignerBar>

                <div className="grid min-h-0 flex-1 lg:grid-cols-[280px_1fr]">
                    <ImageRail cfg={cfg} doubleSided={doubleSided} theme={theme} className="order-2 border-t border-zinc-200 lg:order-1 lg:border-r lg:border-t-0" />
                    <div ref={stageRef} className="relative order-1 h-[52vh] min-h-[360px] [perspective:1400px] lg:order-2 lg:h-auto lg:min-h-0" data-testid="sign-stage">
                        <div key={view} className="sign-flip h-full w-full">
                            <SignCanvas product={product} width={cfg.width} height={cfg.height} artUrl={side.url} options={cfg.options}
                                transform={side.transform} onTransform={(t) => cfg.setTransform(view, t)} doubleSided={doubleSided}
                                theme={theme} sideLabel={doubleSided ? view : undefined} reserveBottom={showGrommets ? 72 : 0}
                                safeZoneIn={handheld ? 0.125 : 0} />
                        </div>
                        {doubleSided && <SideTabs view={view} setView={cfg.setView} theme={theme} />}
                        {doubleSided && <FlipOver view={view} setView={cfg.setView} theme={theme} />}
                        <span className="absolute right-4 top-4 z-10 rounded-full bg-white/90 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 shadow-sm" data-testid="stage-caption">
                            {variant?.label || data?.category?.name}{rateCaption(q)}
                        </span>
                        {isMirrorPrint(cfg.options) && (
                            <span className="absolute right-4 top-12 z-10 rounded-full bg-amber-100 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-amber-800 shadow-sm" data-testid="mirror-print-note">
                                Printed in reverse · reads right through the glass
                            </span>
                        )}
                        {showGrommets && <GrommetControls cfg={cfg} theme={theme} />}
                    </div>
                </div>

                <OptionDock product={product} cfg={cfg} theme={theme} />
            </div>
        </Layout>
    );
}
